import { useRef, useState } from 'react';
import { ResizeHandle } from './ResizeHandle';
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH } from './storage';
import type { NavItem } from './types';

interface Group {
  label: string;
  ids: string[];
}

/**
 * Sidebar sections, each an explicit list of nav ids.
 *
 * NOTE FOR ANYONE ADDING A NAV ITEM: this list is a second, silent gate. An item
 * can exist in `navItems`, pass its capability check and be registered in the
 * view registry, and STILL never appear — because an id in no group here is
 * dropped without warning. That is exactly how Credential Templates was
 * invisible on first run, and nothing failed: no error, no console warning, no
 * test. Add the id here as well as in Workspace.tsx.
 */
const GROUPS: Group[] = [
  { label: 'Workspace', ids: ['home', 'conversations', 'inbox', 'tasks'] },
  { label: 'Build', ids: ['agents', 'skills', 'skills-repository', 'builder'] },
  {
    label: 'Operate',
    ids: [
      'routines',
      'sandbox-runtime-logs',
      // Fleet beside the logs: one is the sandbox infrastructure (pools,
      // bundles, registries), the other its run history — the operator pair.
      'sandbox-fleet',
      'sdk-governance',
      'plans',
      'proxy-vault',
      // Beside the vault, because the two are the pair an operator compares:
      // this one authenticates a CLONE, the vault substitutes on EGRESS.
      'git-credentials',
      'api-keys',
      // Operator SFTP beside the keys: both are how a person reaches the platform from outside.
      'sftp',
    ],
  },
  { label: 'Admin', ids: ['tenants', 'users', 'roles', 'changelog'] },
];

const WIDTH_COLLAPSED = 52;

interface Props {
  items: NavItem[];
  activeNav: string;
  onOpen: (item: NavItem) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Expanded-state width in px (persisted, user-resizable). */
  width: number;
  /** Persist the new expanded width. Fired live during drag. */
  onResize: (width: number) => void;
}

export function Sidebar({
  items,
  activeNav,
  onOpen,
  collapsed,
  onToggleCollapsed,
  width,
  onResize,
}: Props) {
  const byId = new Map(items.map((i) => [i.id, i]));
  // Suppress the width transition while dragging so the rail tracks the cursor
  // 1:1 instead of lagging behind it through the 160ms ease.
  const [dragging, setDragging] = useState(false);
  const dragWidth = useRef(width);

  return (
    <>
      <aside
        style={{
          width: collapsed ? WIDTH_COLLAPSED : width,
          flexShrink: 0,
          background: 'var(--bg-alt)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: dragging ? 'none' : 'width 160ms ease',
        }}
      >
        <Brand collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />
        <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: collapsed ? '6px 0' : '4px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: collapsed ? 2 : 10,
        }}
      >
        {GROUPS.map((group) => ({
          label: group.label,
          items: group.ids
            .map((id) => byId.get(id))
            .filter((item): item is NavItem => !!item),
        }))
          .filter((group) => group.items.length > 0)
          .map((group, groupIdx) => (
            <div key={group.label} style={{ display: 'flex', flexDirection: 'column' }}>
              {collapsed ? (
                groupIdx > 0 && <CollapsedGroupGap />
              ) : (
                <GroupHeader>{group.label}</GroupHeader>
              )}
              {group.items.map((item) => (
                <Row
                  key={item.id}
                  item={item}
                  active={activeNav === item.id}
                  onOpen={onOpen}
                  collapsed={collapsed}
                />
              ))}
            </div>
          ))}
        </nav>
      </aside>
      {/* The draggable seam lives on the rail's right border. Hidden when the
          rail is collapsed — a 52px icon strip isn't meant to be resized; the
          collapse toggle owns that state instead. */}
      {!collapsed && (
        <ResizeHandle
          width={width}
          min={SIDEBAR_MIN_WIDTH}
          max={SIDEBAR_MAX_WIDTH}
          onResize={(w) => {
            if (!dragging) setDragging(true);
            dragWidth.current = w;
            onResize(w);
          }}
          onResizeEnd={(w) => {
            setDragging(false);
            onResize(w);
          }}
        />
      )}
    </>
  );
}

interface BrandProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

function Brand({ collapsed, onToggleCollapsed }: BrandProps) {
  if (collapsed) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          padding: '10px 0 8px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: 'var(--accent)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          S
        </div>
        <CollapseButton collapsed onToggleCollapsed={onToggleCollapsed} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 5,
          background: 'var(--accent)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        S
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', letterSpacing: 0.1, flex: 1 }}>
        shiftagent
      </div>
      <CollapseButton collapsed={false} onToggleCollapsed={onToggleCollapsed} />
    </div>
  );
}

function CollapseButton({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggleCollapsed}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      style={{
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--border)',
        background: 'var(--bg)',
        color: 'var(--text-soft)',
        cursor: 'pointer',
        borderRadius: 4,
        padding: 0,
        fontSize: 14,
        lineHeight: 1,
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--accent)';
        e.currentTarget.style.borderColor = 'var(--accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--text-soft)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      {collapsed ? '›' : '‹'}
    </button>
  );
}

function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '8px 14px 4px',
        fontSize: 10,
        color: 'var(--text-mute)',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

function CollapsedGroupGap() {
  return <div aria-hidden style={{ height: 1, background: 'var(--border)', margin: '6px 10px' }} />;
}

interface RowProps {
  item: NavItem;
  active: boolean;
  onOpen: (item: NavItem) => void;
  collapsed: boolean;
}

function Row({ item, active, onOpen, collapsed }: RowProps) {
  if (collapsed) return <CollapsedRow item={item} active={active} onOpen={onOpen} />;
  return <ExpandedRow item={item} active={active} onOpen={onOpen} />;
}

function ExpandedRow({ item, active, onOpen }: Omit<RowProps, 'collapsed'>) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      title={item.label}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        height: 28,
        padding: '0 12px 0 10px',
        background: active ? 'var(--accent-softer)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-soft)',
        border: 0,
        borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: active ? 500 : 400,
        textAlign: 'left',
        transition: 'background 80ms ease, color 80ms ease',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--bg)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          width: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {item.icon}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {item.label}
      </span>
      {item.badge !== undefined && item.badge > 0 && (
        <span
          style={{
            background: 'var(--accent)',
            color: '#fff',
            fontSize: 10,
            padding: '1px 6px',
            borderRadius: 999,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.3,
          }}
        >
          {item.badge}
        </span>
      )}
    </button>
  );
}

function CollapsedRow({ item, active, onOpen }: Omit<RowProps, 'collapsed'>) {
  // In collapsed mode, the row is centered and uses a filled "tile" for the
  // active state (not a left accent bar, which looks disconnected when the
  // icon is floating in the middle of a 52px column).
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '2px 0',
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(item)}
        title={item.label}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        style={{
          width: 36,
          height: 32,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: active ? 'var(--accent-soft)' : 'transparent',
          color: active ? 'var(--accent)' : 'var(--text-soft)',
          border: 0,
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
          position: 'relative',
          transition: 'background 80ms ease, color 80ms ease',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'var(--bg)';
            e.currentTarget.style.color = 'var(--text)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-soft)';
          }
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {item.icon}
        </span>
        {item.badge !== undefined && item.badge > 0 && (
          <span
            aria-label={`${item.badge} unread`}
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: 'var(--accent)',
              color: '#fff',
              fontSize: 9,
              padding: '0 4px',
              borderRadius: 999,
              minWidth: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {item.badge}
          </span>
        )}
      </button>
    </div>
  );
}
